import { useCallback, useEffect, useRef, useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { MessageList } from '@/components/game/components/MessageList'
import { apiService } from '@/services/api'
import type { Message } from '@/types/message'
import type { Character, Member } from '@/types'

const PAGE = 30

interface MissionViewerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  tableId: string
  /** id da missão, ou 'none' para o Prólogo (mensagens sem missão) */
  missionId: string
  title: string
  currentUserId: string
  characters?: Character[]
  members?: Member[]
}

function sortByTime(messages: Message[]): Message[] {
  return [...messages].sort((a, b) => {
    const ta = new Date(a.timestamp).getTime()
    const tb = new Date(b.timestamp).getTime()
    if (ta !== tb) return ta - tb
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0
  })
}

/**
 * Visualizador somente leitura do histórico de uma missão (ou do Prólogo).
 * Reusa o MessageList com isMaster=false e sem onUpdateMessage — sem input,
 * sem editar/excluir. Marcos de sessão aparecem inline (são mensagens de
 * sistema). Paginação "carregar antigas" reusa o cursor before_id.
 */
export function MissionViewerDialog({
  open,
  onOpenChange,
  tableId,
  missionId,
  title,
  currentUserId,
  characters,
  members,
}: MissionViewerDialogProps) {
  const [messages, setMessages] = useState<Message[]>([])
  const [loading, setLoading] = useState(false)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(false)
  const loadedForRef = useRef<string | null>(null)

  useEffect(() => {
    if (!open) return
    if (loadedForRef.current === missionId) return
    loadedForRef.current = missionId
    setLoading(true)
    setMessages([])
    apiService
      .getTableMessages(tableId, { limit: 100, missionId })
      .then((res) => {
        setMessages(sortByTime(res.messages as Message[]))
        setHasMore(res.has_more)
      })
      .catch((error) => console.error('[MissionViewer] erro ao carregar:', error))
      .finally(() => setLoading(false))
  }, [open, missionId, tableId])

  // Ao fechar, permitir recarregar da próxima vez que abrir.
  useEffect(() => {
    if (!open) loadedForRef.current = null
  }, [open])

  const loadMore = useCallback(async () => {
    if (loadingMore || !hasMore) return
    const oldest = messages[0]
    if (!oldest) return
    setLoadingMore(true)
    try {
      const res = await apiService.getTableMessages(tableId, {
        limit: PAGE,
        beforeId: oldest.id,
        missionId,
      })
      if (res.messages.length > 0) {
        const existing = new Set(messages.map((m) => m.id))
        const fresh = (res.messages as Message[]).filter((m) => !existing.has(m.id))
        if (fresh.length > 0) setMessages((prev) => sortByTime([...fresh, ...prev]))
      }
      setHasMore(res.has_more)
    } catch (error) {
      console.error('[MissionViewer] erro ao paginar:', error)
    } finally {
      setLoadingMore(false)
    }
  }, [loadingMore, hasMore, messages, tableId, missionId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[85vh] max-w-2xl flex-col p-0">
        <DialogHeader className="border-b border-border px-4 py-3">
          <DialogTitle className="text-base">{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1 overflow-hidden">
          {!loading && messages.length === 0 ? (
            <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
              Nenhuma mensagem neste capítulo ainda.
            </div>
          ) : (
            <MessageList
              messages={messages}
              currentUserId={currentUserId}
              chatMode="group"
              characters={characters}
              members={members}
              isMaster={false}
              isLoading={loading}
              onLoadMore={loadMore}
              loadingMore={loadingMore}
              hasMore={hasMore}
            />
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
